import React, { useState } from "react";
import { Star, StarOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Toggles /api/user/saved-hosts/{host_name} for the current user. Only visible
 * to signed-in fan users. Employees/admins can see the button but it's a no-op
 * (we still allow it — server accepts, no harm).
 */
export default function FollowHostButton({ hostName, size = "md", className = "" }) {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const isFollowed = !!(user && user.saved_hosts && user.saved_hosts.includes(hostName));

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast("Sign in to follow hosts", { description: "Continue with Discord to build your fan profile." });
      return;
    }
    setBusy(true);
    try {
      const encoded = encodeURIComponent(hostName);
      if (isFollowed) {
        await api.delete(`/user/saved-hosts/${encoded}`);
      } else {
        await api.post(`/user/saved-hosts/${encoded}`);
      }
      await refresh();
      toast.success(isFollowed ? `Unfollowed ${hostName}` : `Following ${hostName}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const padding = size === "sm" ? "px-2 py-1" : "px-4 py-2.5";
  const text = size === "sm" ? "text-[0.6rem]" : "text-xs";

  return (
    <button
      onClick={toggle}
      disabled={busy}
      className={`inline-flex items-center gap-2 ${padding} border font-mono-ui ${text} uppercase tracking-[0.15em] transition-colors disabled:opacity-50 ${
        isFollowed
          ? "bg-[#FFD60A] text-black border-[#FFD60A]"
          : "border-[var(--ff-border)] text-white hover:border-[#FFD60A] hover:text-[#FFD60A]"
      } ${className}`}
      data-testid={`follow-host-${hostName.replace(/\s+/g, "-").toLowerCase()}`}
    >
      {isFollowed ? <Star size={size === "sm" ? 10 : 14} fill="currentColor" /> : <StarOff size={size === "sm" ? 10 : 14} />}
      {isFollowed ? "Following" : "Follow"}
    </button>
  );
}
